import React, { useState, useMemo, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useEnergySavings,
  useEnergySavingStats,
  useEnergyPartners,
  useEnergySavingExportHistory,
  useBulkExportEnergySavings,
  useBulkRejectEnergySavings,
  useBulkMarkEnergySavingsComplete,
} from "../hooks/useEnergySavings";
import { useLocalStorage } from "../hooks/useLocalStorage";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import CustomToast from "../components/common/CustomToast";
import apiService, { EnergySaving, EnergySavingStatus, EnergyBillConfig } from "../services/api";
import {
  FunnelIcon,
  BoltIcon,
  DocumentArrowDownIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

const SUPPORTED_COUNTRIES: Array<{ code: string; name: string }> = [
  { code: 'AT', name: 'Austria' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'IT', name: 'Italy' },
];

type FilterStatus = "all" | EnergySavingStatus;

const EnergySavings: React.FC = () => {
  const [selectedEnergySavings, setSelectedEnergySavings] = useState<string[]>(
    [],
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedEnergySaving, setSelectedEnergySaving] =
    useState<EnergySaving | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isExportHistoryModalOpen, setIsExportHistoryModalOpen] =
    useState(false);
  const [isCountryActivationModalOpen, setIsCountryActivationModalOpen] =
    useState(false);

  // Energy Bill Country Config state
  const [energyBillConfigs, setEnergyBillConfigs] = useState<EnergyBillConfig[]>([]);
  const [energyBillLoading, setEnergyBillLoading] = useState(false);
  const [togglingCountry, setTogglingCountry] = useState<string | null>(null);
  const [filter, setFilter] = useLocalStorage<FilterStatus>(
    "energy-savings-filter",
    "all",
  );
  const [partnerFilter, setPartnerFilter] = useLocalStorage<string>(
    "energy-savings-partner-filter",
    "all",
  );
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "energy-savings-search-query",
    "",
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);

  const { data: energySavingsResponse, isLoading } = useEnergySavings({
    limit: itemsPerPage,
    page: currentPage,
    search: searchQuery || undefined,
    status: filter !== "all" ? filter : undefined,
    energyPartner: partnerFilter !== "all" ? partnerFilter : undefined,
  });

  const { data: stats } = useEnergySavingStats();
  const { data: partnersData } = useEnergyPartners();
  const { data: exportHistoryData } = useEnergySavingExportHistory(10);

  const bulkExport = useBulkExportEnergySavings();
  const bulkReject = useBulkRejectEnergySavings();
  const bulkMarkComplete = useBulkMarkEnergySavingsComplete();

  const energySavings = useMemo(
    () => energySavingsResponse?.data || [],
    [energySavingsResponse?.data],
  );
  const totalItems = energySavingsResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const partners = partnersData?.data || [];
  const exportHistory = exportHistoryData?.data || [];

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, partnerFilter, searchQuery]);

  // Clear selections when data changes
  React.useEffect(() => {
    setSelectedEnergySavings([]);
  }, [energySavingsResponse]);

  const handleSelectAll = () => {
    if (selectedEnergySavings.length === energySavings.length) {
      setSelectedEnergySavings([]);
    } else {
      setSelectedEnergySavings(energySavings.map((es) => es._id));
    }
  };

  const handleSelectEnergySaving = (id: string) => {
    if (selectedEnergySavings.includes(id)) {
      setSelectedEnergySavings(
        selectedEnergySavings.filter((esid) => esid !== id),
      );
    } else {
      setSelectedEnergySavings([...selectedEnergySavings, id]);
    }
  };

  const handleViewDetails = (energySaving: EnergySaving) => {
    setSelectedEnergySaving(energySaving);
    setIsDetailModalOpen(true);
  };

  const handleExport = () => {
    if (selectedEnergySavings.length === 0) return;
    bulkExport.mutate(selectedEnergySavings);
  };

  const handleOpenRejectModal = () => {
    if (selectedEnergySavings.length === 0) return;
    setRejectReason("");
    setIsRejectModalOpen(true);
  };

  const handleReject = () => {
    if (selectedEnergySavings.length === 0 || !rejectReason.trim()) return;
    bulkReject.mutate(
      { energySavingIds: selectedEnergySavings, reason: rejectReason },
      { onSuccess: () => setIsRejectModalOpen(false) },
    );
  };

  const handleMarkComplete = () => {
    if (selectedEnergySavings.length === 0) return;
    bulkMarkComplete.mutate(selectedEnergySavings);
  };

  const getStatusBadge = (status: EnergySavingStatus) => {
    const badges: Record<
      EnergySavingStatus,
      { bg: string; icon: React.ReactNode; label: string }
    > = {
      open: {
        bg: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
        icon: <ClockIcon className="h-4 w-4 mr-1" />,
        label: "Open",
      },
      in_progress: {
        bg: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
        icon: <ArrowPathIcon className="h-4 w-4 mr-1" />,
        label: "In Progress",
      },
      complete: {
        bg: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
        icon: <CheckCircleIcon className="h-4 w-4 mr-1" />,
        label: "Complete",
      },
      rejected: {
        bg: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
        icon: <XCircleIcon className="h-4 w-4 mr-1" />,
        label: "Rejected",
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if any selected energy saving can be exported (only open status)
  const canExport = useMemo(() => {
    return selectedEnergySavings.some((id) => {
      const es = energySavings.find((e) => e._id === id);
      return es?.status === "open";
    });
  }, [selectedEnergySavings, energySavings]);

  // Check if any selected energy saving can be marked as complete (only in_progress status)
  const canMarkComplete = useMemo(() => {
    return selectedEnergySavings.some((id) => {
      const es = energySavings.find((e) => e._id === id);
      return es?.status === "in_progress";
    });
  }, [selectedEnergySavings, energySavings]);

  // Check if any selected energy saving can be rejected (open or in_progress status)
  const canReject = useMemo(() => {
    return selectedEnergySavings.some((id) => {
      const es = energySavings.find((e) => e._id === id);
      return es?.status === "open" || es?.status === "in_progress";
    });
  }, [selectedEnergySavings, energySavings]);

  // Energy Bill Country Config helpers
  const getEnabledMap = useCallback((): Record<string, boolean> => {
    const map: Record<string, boolean> = {};
    for (const cfg of energyBillConfigs) {
      map[cfg.country.toUpperCase()] = cfg.enabled;
    }
    return map;
  }, [energyBillConfigs]);

  const loadEnergyBillConfigs = useCallback(async () => {
    setEnergyBillLoading(true);
    try {
      const configs = await apiService.getAllEnergyBillConfigs();
      setEnergyBillConfigs(Array.isArray(configs) ? configs : []);
    } catch {
      toast.custom(
        <CustomToast message="Failed to load energy bill configs" type="error" />,
        { duration: 4000 },
      );
    } finally {
      setEnergyBillLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEnergyBillConfigs();
  }, [loadEnergyBillConfigs]);

  const handleToggleCountry = async (country: string, countryName: string, currentEnabled: boolean) => {
    setTogglingCountry(country);
    try {
      await apiService.setEnergyBillCountryConfig(country, !currentEnabled, countryName);
      setEnergyBillConfigs(prev => {
        const exists = prev.find(c => c.country === country);
        if (exists) {
          return prev.map(c => c.country === country ? { ...c, enabled: !currentEnabled } : c);
        }
        return [...prev, { country, countryName, enabled: !currentEnabled }];
      });
      toast.custom(
        <CustomToast
          message={`Energy bill ${!currentEnabled ? 'enabled' : 'disabled'} for ${countryName}`}
          type={!currentEnabled ? 'success' : 'warning'}
        />,
        { duration: 3000 },
      );
    } catch (err: any) {
      toast.custom(
        <CustomToast
          message={`Failed to update ${countryName}: ${err.response?.data?.message || err.message}`}
          type="error"
        />,
        { duration: 5000 },
      );
    } finally {
      setTogglingCountry(null);
    }
  };

  const enabledMap = getEnabledMap();
  const enabledCount = SUPPORTED_COUNTRIES.filter(c => enabledMap[c.code]).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Energy Savings
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage customer energy saving requests
          </p>
        </div>
        <div className="mt-2 sm:mt-0 flex items-center gap-2">
          <button
            onClick={() => setIsCountryActivationModalOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Cog6ToothIcon className="h-4 w-4 mr-2" />
            Country Settings
          </button>
          <button
            onClick={() => setIsExportHistoryModalOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export History
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">Open</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.open || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              In Progress
            </div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {stats.inProgress || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Complete
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.complete || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Amount
            </div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {formatCurrency(stats.totalEuroAmount || 0)}
            </div>
          </div>
        </div>
      )}

      {/* Partner Stats */}
      {stats?.byPartner && stats.byPartner.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            By Energy Partner
          </h3>
          <div className="flex flex-wrap gap-3">
            {stats.byPartner.map((partner) => (
              <div
                key={partner.partner}
                className="inline-flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <BoltIcon className="h-4 w-4 text-yellow-500 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {partner.partner}
                </span>
                <span className="mx-2 text-gray-400">|</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {partner.count} requests
                </span>
                <span className="mx-2 text-gray-400">|</span>
                <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                  {formatCurrency(partner.totalAmount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search by customer name or email..."
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterStatus)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="complete">Complete</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Partner Filter */}
            <div className="flex items-center space-x-2">
              <BoltIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="all">All Partners</option>
                {partners.map((partner) => (
                  <option key={partner} value={partner}>
                    {partner}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedEnergySavings.length > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedEnergySavings.length} energy saving(s) selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleExport}
                disabled={!canExport || bulkExport.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                {bulkExport.isPending ? "Exporting..." : "Export"}
              </button>
              <button
                onClick={handleMarkComplete}
                disabled={!canMarkComplete || bulkMarkComplete.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                {bulkMarkComplete.isPending ? "Processing..." : "Mark Complete"}
              </button>
              <button
                onClick={handleOpenRejectModal}
                disabled={!canReject || bulkReject.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Energy Savings Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : energySavings.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        selectedEnergySavings.length === energySavings.length &&
                        energySavings.length > 0
                      }
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Energy Contract
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {energySavings.map((es) => (
                  <tr
                    key={es._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedEnergySavings.includes(es._id)}
                        onChange={() => handleSelectEnergySaving(es._id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900 dark:text-white">
                        {es.energySavingId}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {es.customerSnapshot.firstName}{" "}
                          {es.customerSnapshot.lastName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {es.customerSnapshot.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(es.euroAmount ?? es.amount ?? es.pointsAmount * 0.01)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {es.pointsAmount.toLocaleString()} EP
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {es.energyContractPartner}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {es.energyContractNumber}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(es.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(es.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetails(es)}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <BoltIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No energy savings found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all" || partnerFilter !== "all"
              ? "Try adjusting your search or filters"
              : "No energy saving requests yet"}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedEnergySaving && isDetailModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsDetailModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-yellow-500 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BoltIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Energy Saving Details
                    </h3>
                    <p className="text-sm text-yellow-100">
                      {selectedEnergySaving.energySavingId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsDetailModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    {getStatusBadge(selectedEnergySaving.status)}
                  </div>

                  {/* Amount */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Amount
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Euro Amount
                        </label>
                        <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                          {formatCurrency(selectedEnergySaving.euroAmount ?? selectedEnergySaving.amount ?? selectedEnergySaving.pointsAmount * 0.01)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Points
                        </label>
                        <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedEnergySaving.pointsAmount.toLocaleString()}{" "}
                          EP
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Customer
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedEnergySaving.customerSnapshot.firstName}{" "}
                          {selectedEnergySaving.customerSnapshot.lastName}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Email
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white break-all">
                          {selectedEnergySaving.customerSnapshot.email}
                        </p>
                      </div>
                      {selectedEnergySaving.customerSnapshot.street && (
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Address
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedEnergySaving.customerSnapshot.street}{" "}
                            {selectedEnergySaving.customerSnapshot.houseNumber}
                            {selectedEnergySaving.customerSnapshot
                              .addressAddition &&
                              `, ${selectedEnergySaving.customerSnapshot.addressAddition}`}
                            <br />
                            {selectedEnergySaving.customerSnapshot.zipCode}{" "}
                            {selectedEnergySaving.customerSnapshot.city}
                            {selectedEnergySaving.customerSnapshot.country &&
                              `, ${selectedEnergySaving.customerSnapshot.country}`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Energy Contract */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Energy Contract
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Energy Partner
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedEnergySaving.energyContractPartner}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Contract Number
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                          {selectedEnergySaving.energyContractNumber}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status History */}
                  {selectedEnergySaving.statusHistory &&
                    selectedEnergySaving.statusHistory.length > 0 && (
                      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          Status History
                        </h4>
                        <div className="space-y-2">
                          {selectedEnergySaving.statusHistory.map(
                            (history, index) => (
                              <div
                                key={index}
                                className="flex items-start space-x-3"
                              >
                                <div className="flex-shrink-0">
                                  {getStatusBadge(history.status)}
                                </div>
                                <div>
                                  <p className="text-sm text-gray-900 dark:text-white">
                                    {formatDate(history.changedAt.toString())}
                                  </p>
                                  {history.reason && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {history.reason}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                  {/* Rejection Reason */}
                  {selectedEnergySaving.rejectionReason && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-800 dark:text-red-200">
                          Rejection Reason
                        </span>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {selectedEnergySaving.rejectionReason}
                      </p>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Timestamps
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Created
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedEnergySaving.createdAt)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Updated
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedEnergySaving.updatedAt)}
                        </p>
                      </div>
                      {selectedEnergySaving.exportedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Exported
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(
                              selectedEnergySaving.exportedAt.toString(),
                            )}
                          </p>
                        </div>
                      )}
                      {selectedEnergySaving.processedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Processed
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(
                              selectedEnergySaving.processedAt.toString(),
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsRejectModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-red-600 px-6 py-4">
                <h3 className="text-xl font-semibold text-white">
                  Reject Energy Savings
                </h3>
                <p className="text-sm text-red-100">
                  {selectedEnergySavings.length} request(s) will be rejected
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter the reason for rejection..."
                />
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsRejectModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || bulkReject.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkReject.isPending ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Country Activation Modal */}
      {isCountryActivationModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsCountryActivationModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-yellow-500 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BoltIcon className="h-6 w-6 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Energy Bill — Country Activation
                    </h3>
                    <p className="text-sm text-yellow-100">
                      {enabledCount} / {SUPPORTED_COUNTRIES.length} countries enabled
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={loadEnergyBillConfigs}
                    disabled={energyBillLoading}
                    className="flex items-center px-3 py-1.5 text-sm bg-yellow-400 hover:bg-yellow-300 text-yellow-900 rounded-md transition-colors disabled:opacity-50"
                  >
                    {energyBillLoading ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-800 mr-1.5" />
                    ) : (
                      <GlobeAltIcon className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Refresh
                  </button>
                  <button
                    onClick={() => setIsCountryActivationModalOpen(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[60vh] overflow-y-auto">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Enable or disable the energy bill feature per country. Disabled countries cannot submit energy saving requests.
                </p>

                {energyBillLoading && energyBillConfigs.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-gray-400">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mr-3" />
                    Loading configs...
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {SUPPORTED_COUNTRIES.map(({ code, name }) => {
                      const isEnabled = enabledMap[code] === true;
                      const isToggling = togglingCountry === code;

                      return (
                        <div
                          key={code}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            isEnabled
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                              : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center space-x-2 min-w-0">
                            <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-gray-200 dark:bg-gray-600 text-xs font-mono font-bold text-gray-600 dark:text-gray-300 flex-shrink-0">
                              {code}
                            </span>
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {name}
                            </p>
                          </div>

                          <button
                            onClick={() => handleToggleCountry(code, name, isEnabled)}
                            disabled={isToggling}
                            className={`relative inline-flex flex-shrink-0 h-5 w-10 ml-2 rounded-full cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                              isEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                            title={isEnabled ? `Disable for ${name}` : `Enable for ${name}`}
                          >
                            {isToggling ? (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                              </span>
                            ) : (
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${
                                  isEnabled ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                  Changes take effect immediately. Customers in disabled countries will receive an error when trying to submit energy saving requests.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export History Modal */}
      {isExportHistoryModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsExportHistoryModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-yellow-500 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">
                  Export History
                </h3>
                <button
                  onClick={() => setIsExportHistoryModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-96 overflow-y-auto">
                {exportHistory.length > 0 ? (
                  <div className="space-y-3">
                    {exportHistory.map((item) => (
                      <div
                        key={item.batchId}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-mono text-gray-900 dark:text-white">
                            {item.batchId}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(item.exportedAt)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.count} requests
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCurrency(item.totalAmount)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400">
                    No exports yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergySavings;
